import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { SITE } from "@/lib/site";
import { Mail, MapPin, MessageCircle, Phone } from "lucide-react";


export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: `Contacto — ${SITE.brand.name}` },
      { name: "description", content: "Escríbenos por WhatsApp, teléfono o email. Atención al cliente 24/7." },
      { property: "og:title", content: `Contacto — ${SITE.brand.name}` },
      { property: "og:url", content: "/contact" },
    ],
    links: [{ rel: "canonical", href: "https://www.mostrandomexico.mx/contact" }],
  }),
  component: ContactPage,
});

function ContactPage() {
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [reference, setReference] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    setStatus("sending");
    setErrorMsg(null);
    try {
      const res = await fetch("/api/public/contact", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          name: String(data.get("name") ?? ""),
          email: String(data.get("email") ?? ""),
          phone: String(data.get("phone") ?? ""),
          message: String(data.get("message") ?? ""),
          lang: "es",
        }),
      });
      const out = (await res.json().catch(() => null)) as
        | { ok?: boolean; reference?: string; error?: string }
        | null;
      if (!res.ok || !out?.ok) {
        setStatus("error");
        setErrorMsg(
          out?.error === "rate_limited"
            ? "Recibimos varias solicitudes desde tu conexión. Inténtalo de nuevo en un rato o escríbenos por WhatsApp."
            : out?.error === "invalid_input"
              ? "Revisa tus datos: necesitamos un nombre, un correo válido y un mensaje."
              : "No pudimos registrar tu solicitud. Inténtalo de nuevo o escríbenos por WhatsApp.",
        );
        return;
      }
      setReference(out.reference ?? null);
      setStatus("sent");
      form.reset();
    } catch {
      setStatus("error");
      setErrorMsg("No pudimos registrar tu solicitud. Inténtalo de nuevo o escríbenos por WhatsApp.");
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-5xl px-4 py-16 sm:px-6 lg:px-8">
        <header className="mb-10 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Contacto</p>
          <h1 className="mt-3 font-display text-4xl font-semibold sm:text-5xl">
            ¡Ponte en contacto!
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-sm text-muted-foreground">
            Envíanos tu solicitud desde el formulario y nuestro equipo la recibirá al instante, o
            escríbenos directamente por WhatsApp, teléfono o email. Te responderemos dentro de las
            próximas 24 horas.
          </p>

        </header>

        <div className="grid gap-8 md:grid-cols-[1fr_1.2fr]">
          <aside className="space-y-4">
            <ContactCard icon={MapPin} title="Dirección">
              {SITE.contact.address}
            </ContactCard>
            <ContactCard icon={Phone} title="Teléfono" href={`tel:${SITE.contact.phoneTel}`}>
              {SITE.contact.phone}
            </ContactCard>
            <ContactCard icon={MessageCircle} title="WhatsApp 24/7" href={SITE.contact.whatsappUrl}>
              Atención inmediata por WhatsApp
            </ContactCard>
            <ContactCard icon={Mail} title="Email" href={`mailto:${SITE.contact.email}`}>
              {SITE.contact.email}
            </ContactCard>
          </aside>

          {status === "sent" ? (
            <div className="rounded-3xl border border-border bg-card p-8 shadow-card">
              <h2 className="font-display text-2xl font-semibold">¡Solicitud recibida!</h2>
              <p className="mt-3 text-sm text-muted-foreground">
                Tu solicitud ya está registrada y nuestro equipo la revisará en breve. También te
                enviamos un correo de confirmación.
              </p>
              {reference ? (
                <p className="mt-3 text-sm">
                  Folio de seguimiento: <span className="font-semibold">{reference}</span>
                </p>
              ) : null}
              <div className="mt-6 flex flex-wrap gap-3">
                <a
                  href={SITE.contact.whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex h-11 items-center justify-center rounded-xl bg-primary px-5 text-sm font-semibold text-primary-foreground"
                >
                  Hablar por WhatsApp
                </a>
                <button
                  type="button"
                  onClick={() => setStatus("idle")}
                  className="inline-flex h-11 items-center justify-center rounded-xl border border-border px-5 text-sm font-semibold"
                >
                  Enviar otra solicitud
                </button>
              </div>
            </div>
          ) : (
            <form
              className="space-y-4 rounded-3xl border border-border bg-card p-6 shadow-card sm:p-8"
              onSubmit={handleSubmit}
            >
              <Field label="Nombre" name="name" required />
              <Field label="Email" name="email" type="email" required />
              <Field label="Teléfono (opcional)" name="phone" type="tel" />
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Mensaje
                </label>
                <textarea
                  name="message"
                  required
                  rows={5}
                  maxLength={2000}
                  className="mt-1 w-full rounded-xl border border-border bg-background px-4 py-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                />
              </div>
              {errorMsg ? (
                <p className="rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                  {errorMsg}
                </p>
              ) : null}
              <button
                type="submit"
                disabled={status === "sending"}
                className="inline-flex h-12 w-full items-center justify-center rounded-xl bg-primary text-sm font-semibold text-primary-foreground shadow-glow transition-transform hover:scale-[1.01] disabled:opacity-60"
              >
                {status === "sending" ? "Enviando…" : "Enviar solicitud"}
              </button>
              <p className="text-center text-xs text-muted-foreground">
                Tu solicitud llega directamente a nuestro equipo y recibirás un correo de
                confirmación. Si prefieres una respuesta inmediata, escríbenos por WhatsApp.
              </p>

            </form>
          )}

        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

function ContactCard({
  icon: Icon,
  title,
  href,
  children,
}: {
  icon: typeof Mail;
  title: string;
  href?: string;
  children: React.ReactNode;
}) {
  const inner = (
    <div className="flex gap-4 rounded-2xl border border-border bg-card p-5 shadow-card transition hover:border-primary/40">
      <Icon className="h-5 w-5 flex-shrink-0 text-primary" />
      <div>
        <h3 className="text-sm font-semibold">{title}</h3>
        <p className="mt-1 text-sm text-muted-foreground">{children}</p>
      </div>
    </div>
  );
  return href ? (
    <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer">
      {inner}
    </a>
  ) : (
    inner
  );
}

function Field({ label, name, type = "text", required }: { label: string; name: string; type?: string; required?: boolean }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </label>
      <input
        name={name}
        type={type}
        required={required}
        className="mt-1 h-11 w-full rounded-xl border border-border bg-background px-4 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
      />
    </div>
  );
}
